/**
 * Predictions + instance map → per-series segment marks (pure).
 *
 * Only MASK-LOCALIZED findings produce marks: each must carry an a2z-rle-v1
 * segmentation, be detected at its own `recommended_threshold`, and name at
 * least one `top_slices` entry. This stays inert for classification devices
 * without any device-name or finding-name checks. Multiple semantic findings
 * are combined, and their counts are summed when they land on the same slice.
 *
 * Marks legitimately span several series: a CADe device may concatenate series
 * into one volume before inference (device-engine inference.py
 * concatenate_series), so `analyzed_series_id` on the worklist row is the
 * authoritative stack. A mark resolving OUTSIDE that set is a regression, not
 * an expected case — all 603 segment UIDs on the reference cohort resolved
 * inside it — so it warns loudly while still rendering.
 *
 * ponytail: slice-level marks only. The payload carries SOPInstanceUIDs and no
 * in-plane coordinate, so two segments on one slice are indistinguishable here.
 * Upgrade to a real per-segment marker if device-engine emits centroids/bbox.
 * ponytail: no size floor. Voxel counts never reach the frontend, so noise
 * segments cannot be filtered client-side; a floor belongs in the engine, which
 * deliberately declined one at n=44.
 */

import { groupSegmentationSlices, normalizeSliceUid } from "./segmentationSlices";
import type { NativePredictions } from "../types/predictions";

export interface SegmentationMark {
  seriesId: string;
  /** 0-based index within that series' slice list. */
  sliceIndex: number;
  segments: number;
}

export interface SegmentationMarkSet {
  /** Segment count straight from the payload — NOT the sum of resolved marks,
   *  so an unresolvable slice shows up as a gap instead of shrinking the total. */
  totalSegments: number;
  /** Largest-first, one per distinct slice. */
  marks: SegmentationMark[];
}

/** Slice lookup shape of `instanceSliceInfoMapAtom`: seriesId → uid → { index }. */
type InstanceSliceIndex = Record<string, Record<string, { index: number }>>;

type Warn = (message: string) => void;

const noop: Warn = () => {};

export function resolveSegmentationMarks(
  predictions: NativePredictions,
  instanceSliceInfoMap: InstanceSliceIndex,
  analyzedSeriesId: string | undefined,
  warn: Warn = noop,
): SegmentationMarkSet | null {
  const candidates: [string, string[]][] = [];
  for (const [condition, pred] of Object.entries(predictions)) {
    const topSlices = pred?.top_slices;
    if (
      pred?.segmentation?.format !== "a2z-rle-v1" ||
      !Array.isArray(topSlices) ||
      topSlices.length === 0
    )
      continue;
    const recommended = pred.recommended_threshold;
    if (!recommended) {
      // No fallback: an OP guess here would invent a detection.
      warn(
        `[segmentMarks] ${condition} carries ${topSlices.length} top_slices but no ` +
          `recommended_threshold — skipped rather than defaulted to an operating point.`,
      );
      continue;
    }
    if (pred.operating_points?.[recommended] !== true) continue;
    candidates.push([condition, topSlices]);
  }

  if (candidates.length === 0) return null;
  const conditions = candidates.map(([condition]) => condition);
  const groups = candidates.flatMap(([condition, topSlices]) =>
    groupSegmentationSlices(topSlices).map((group) => ({ ...group, condition })),
  );
  const totalSegments = groups.reduce((sum, group) => sum + group.segments, 0);

  const analyzed = new Set((analyzedSeriesId ?? "").split("+").filter((s) => s.length > 0));
  if (analyzed.size === 0) {
    warn(
      `[segmentMarks] no analyzed_series_id on the worklist row for ${conditions.join(", ")}; ` +
        `cannot verify marks against the analysed stack.`,
    );
  }

  const marksBySlice = new Map<string, SegmentationMark>();
  for (const group of groups) {
    let resolved: SegmentationMark | null = null;
    for (const [seriesId, byUid] of Object.entries(instanceSliceInfoMap)) {
      const info = byUid[group.uid] ?? byUid[`${group.uid}.dcm`];
      if (info) {
        resolved = { seriesId, sliceIndex: info.index, segments: group.segments };
        break;
      }
    }
    if (!resolved) {
      warn(
        `[segmentMarks] ${group.condition} slice ${normalizeSliceUid(group.uid)} matches no ` +
          `instance in this study — ${group.segments} segment(s) not navigable.`,
      );
      continue;
    }
    if (analyzed.size > 0 && !analyzed.has(resolved.seriesId)) {
      warn(
        `[segmentMarks] ${group.condition} mark resolved to series ${resolved.seriesId}, which is ` +
          `NOT in analyzed_series_id — engine index→UID mapping may have drifted.`,
      );
    }
    const key = `${resolved.seriesId}\0${resolved.sliceIndex}`;
    const existing = marksBySlice.get(key);
    marksBySlice.set(key, {
      ...resolved,
      segments: resolved.segments + (existing?.segments ?? 0),
    });
  }

  return { totalSegments, marks: [...marksBySlice.values()] };
}

/** Key slices (SOP Instance UIDs) as slice-bar marks, one per distinct slice;
 * `segments` counts the key slices sharing it. null when there are none, or
 * before the study metadata (the UID map) has loaded. */
export function resolveKeySliceMarks(
  sopInstanceUids: readonly string[],
  instanceSliceInfoMap: InstanceSliceIndex,
  warn: Warn = noop,
): SegmentationMarkSet | null {
  if (sopInstanceUids.length === 0 || Object.keys(instanceSliceInfoMap).length === 0) return null;
  const marksBySlice = new Map<string, SegmentationMark>();
  for (const uid of sopInstanceUids) {
    const entry = Object.entries(instanceSliceInfoMap).find(([, byUid]) => byUid[uid]);
    if (!entry) {
      warn(`[keySliceMarks] key slice ${uid} matches no instance in this study.`);
      continue;
    }
    const [seriesId, byUid] = entry;
    const key = `${seriesId}|${byUid[uid].index}`;
    const mark = marksBySlice.get(key);
    marksBySlice.set(
      key,
      mark
        ? { ...mark, segments: mark.segments + 1 }
        : { seriesId, sliceIndex: byUid[uid].index, segments: 1 },
    );
  }
  return { totalSegments: sopInstanceUids.length, marks: [...marksBySlice.values()] };
}
